/**
 * Token 存储。
 *
 * 硬性要求：**后台 token 与会员 token 分开存储**。
 *  - 后台：`wd_admin_access_token` / `wd_admin_refresh_token`
 *  - 会员（shop 端）：`wd_member_access_token` / `wd_member_refresh_token`
 *
 * 说明：后台是 PC 管理端、没有小程序场景，accessToken 放内存会导致刷新页面即掉线，
 * 体验不可接受，因此后台 accessToken 持久化到 sessionStorage（关标签页即失效），
 * refreshToken 放 localStorage。会员侧按契约「accessToken 放内存」保留内存实现。
 */

const ADMIN_ACCESS_KEY = 'wd_admin_access_token'
const ADMIN_REFRESH_KEY = 'wd_admin_refresh_token'
const MEMBER_ACCESS_KEY = 'wd_member_access_token'
const MEMBER_REFRESH_KEY = 'wd_member_refresh_token'

function safeSession(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

function safeLocal(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null
    return window.localStorage
  } catch {
    return null
  }
}

/** 会员 accessToken 内存态（契约要求：不放 localStorage） */
let memberAccessTokenInMemory: string | null = null

// ---------------- 后台 ----------------

export function getAdminAccessToken(): string | null {
  return safeSession()?.getItem(ADMIN_ACCESS_KEY) ?? null
}

export function setAdminTokens(accessToken: string, refreshToken?: string | null): void {
  safeSession()?.setItem(ADMIN_ACCESS_KEY, accessToken)
  if (refreshToken) safeLocal()?.setItem(ADMIN_REFRESH_KEY, refreshToken)
}

export function getAdminRefreshToken(): string | null {
  return safeLocal()?.getItem(ADMIN_REFRESH_KEY) ?? null
}

export function clearAdminTokens(): void {
  safeSession()?.removeItem(ADMIN_ACCESS_KEY)
  safeLocal()?.removeItem(ADMIN_REFRESH_KEY)
}

// ---------------- 会员 ----------------

export function getMemberAccessToken(): string | null {
  return memberAccessTokenInMemory ?? safeSession()?.getItem(MEMBER_ACCESS_KEY) ?? null
}

export function setMemberTokens(accessToken: string, refreshToken?: string | null): void {
  memberAccessTokenInMemory = accessToken
  safeSession()?.setItem(MEMBER_ACCESS_KEY, accessToken)
  if (refreshToken) safeLocal()?.setItem(MEMBER_REFRESH_KEY, refreshToken)
}

export function getMemberRefreshToken(): string | null {
  return safeLocal()?.getItem(MEMBER_REFRESH_KEY) ?? null
}

export function clearMemberTokens(): void {
  memberAccessTokenInMemory = null
  safeSession()?.removeItem(MEMBER_ACCESS_KEY)
  safeLocal()?.removeItem(MEMBER_REFRESH_KEY)
}

/** 退出登录时清理两侧（后台登出不影响会员登录态时请只调 clearAdminTokens） */
export function clearAllTokens(): void {
  clearAdminTokens()
  clearMemberTokens()
}

export const TOKEN_KEYS = {
  ADMIN_ACCESS_KEY,
  ADMIN_REFRESH_KEY,
  MEMBER_ACCESS_KEY,
  MEMBER_REFRESH_KEY,
} as const
